/**
 * components/landing/home/AIIntelligence.jsx — Section 4.
 *
 * Split layout — left column: value narrative + feature bullets; right
 * column: a live-ish AI Mentor conversation panel.  The panel cycles
 * through a scripted mini-thread every ~8s, showing streaming, citations,
 * confidence, follow-up chips and knowledge retrieval.
 */

import { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { Container, Eyebrow, SectionHeading, GradientButton, GhostButton } from '../design-system'
import { MockDashboardChrome, useInView } from './_shared'
import { Sparkles, BookOpen, ShieldCheck, MessageSquare, ChevronRight } from 'lucide-react'

const BULLETS = [
  { icon: Sparkles,    title: 'Grounded RAG',       body: 'Every answer cites the exact source chunk it came from. Confidence is scored, never asserted.' },
  { icon: BookOpen,    title: 'Knowledge retrieval',body: 'Multi-format ingestion (PDF, DOCX, CSV, MD, HTML, JSON) plus platform-derived auto-docs.' },
  { icon: ShieldCheck, title: 'Governance-first',   body: 'Draft → review → approved → published state machine. Rollbacks and version history baked in.' },
  { icon: MessageSquare, title: 'Conversational UX',body: 'Streaming answers, follow-up chips, and starter prompts derived from your Knowledge Base.' },
]

const SCRIPT = [
  {
    q: 'What is the current bias on BTC/USDT?',
    a: 'BTC broke the 4H resistance at $63.4k with rising volume. Order block held on the retest. Bias is bullish while price stays above $62.8k.',
    conf: 74,
    cites: ['HTF bias · 4H structure', 'Volume profile', 'Order block — 63.0k'],
    chips: ['What invalidates this?', 'Risk to reward for a long?', 'Show me on chart'],
  },
  {
    q: 'Explain the auto-documentation flow.',
    a: 'The engine scans your platform (APIs, UI, DB) and produces 20 volumes of grounded documentation. Every chapter runs draft → review → approved → published with version snapshots on every regenerate.',
    conf: 91,
    cites: ['Auto-Doc engine · v1', 'Governance chapter', 'Chapter state machine'],
    chips: ['Who can approve?', 'What triggers regen?', 'How to rollback'],
  },
  {
    q: 'How do I add a new trading pair?',
    a: 'Trading pairs are configured under Admin › Markets. New pairs inherit tenant risk limits automatically and appear in the scanner within one minute.',
    conf: 82,
    cites: ['Markets admin', 'Risk inheritance', 'Scanner refresh'],
    chips: ['Which exchanges support X?', 'Set custom risk', 'Delist a pair'],
  },
]

const AIIntelligence = () => {
  const [idx, setIdx] = useState(0)
  const [phase, setPhase] = useState('ask') // 'ask' | 'think' | 'answer' | 'idle'
  const [typed, setTyped] = useState('')
  const ref = useRef(null)
  const inView = useInView(ref)

  useEffect(() => {
    if (!inView) return
    let cancelled = false
    let timers = []
    const script = SCRIPT[idx]
    setPhase('ask'); setTyped('')
    timers.push(setTimeout(() => !cancelled && setPhase('think'), 900))
    timers.push(setTimeout(() => !cancelled && setPhase('answer'), 1900))
    // Type out answer
    const startType = 1900
    for (let i = 0; i <= script.a.length; i += 3) {
      timers.push(setTimeout(() => !cancelled && setTyped(script.a.slice(0, i)), startType + i * 18))
    }
    // Move to next script after idle
    timers.push(setTimeout(() => !cancelled && setIdx((n) => (n + 1) % SCRIPT.length), startType + script.a.length * 18 + 4500))
    return () => { cancelled = true; timers.forEach(clearTimeout) }
  }, [idx, inView])

  const script = SCRIPT[idx]

  return (
    <section ref={ref} className="py-24 md:py-40">
      <Container>
        <div className="grid gap-12 lg:grid-cols-[1fr,1.15fr] items-center">
          <div className="space-y-6">
            <Eyebrow>AI Intelligence</Eyebrow>
            <SectionHeading
              title={<>Answers that <span className="text-brand-gold">cite their sources</span>.<br />Confidence that’s <span className="text-brand-emerald">scored, not asserted</span>.</>}
              subtitle="Kalki Ai Cloud’s AI Mentor is a governed RAG assistant. Provider-agnostic, tenant-scoped, and grounded in the operator’s own knowledge base."
            />
            <div className="grid gap-3 sm:grid-cols-2 max-w-xl">
              {BULLETS.map((b) => (
                <div key={b.title} className="rounded-xl border border-white/5 bg-white/[0.02] p-4">
                  <div className="flex items-center gap-2 mb-1.5">
                    <b.icon className="h-4 w-4 text-brand-gold" />
                    <div className="text-[13px] font-semibold text-white">{b.title}</div>
                  </div>
                  <div className="text-[12px] text-zinc-400 leading-relaxed">{b.body}</div>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-3 pt-1">
              <GradientButton href="/ai">See AI capabilities</GradientButton>
              <GhostButton href="/contact">Talk to us</GhostButton>
            </div>
          </div>

          {/* AI panel */}
          <MockDashboardChrome innerClass="p-4 md:p-6 min-h-[440px] flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="h-7 w-7 rounded-lg bg-brand-gold/15 text-brand-gold flex items-center justify-center"><Sparkles className="h-3.5 w-3.5" /></div>
                <div className="text-[13px] font-semibold text-white">AI Mentor</div>
              </div>
              <div className="inline-flex items-center gap-1.5 rounded-full border border-brand-emerald/25 bg-brand-emerald/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-brand-emerald">
                <span className={clsx('h-1.5 w-1.5 rounded-full bg-brand-emerald', phase !== 'idle' && 'animate-pulse')} />
                {phase === 'think' ? 'thinking' : phase === 'answer' ? 'streaming' : 'listening'}
              </div>
            </div>

            <div className="flex-1 flex flex-col gap-3">
              {/* Question */}
              <div className="self-end max-w-[85%] rounded-2xl rounded-br-md bg-white/[0.04] px-4 py-2.5 text-sm text-zinc-200">
                {script.q}
              </div>

              {/* Retrieval */}
              {(phase === 'think' || phase === 'answer') && (
                <div className="self-start max-w-[92%] rounded-xl border border-white/5 bg-white/[0.02] px-3 py-2 space-y-1">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Retrieving from knowledge base…</div>
                  <div className="space-y-1">
                    {script.cites.map((c, i) => (
                      <div key={c} className={clsx('flex items-center gap-2 text-[11px]', i < (phase === 'answer' ? script.cites.length : 1) ? 'text-zinc-300' : 'text-zinc-600')}>
                        <div className={clsx('h-1.5 w-1.5 rounded-full', i < (phase === 'answer' ? script.cites.length : 1) ? 'bg-brand-emerald' : 'bg-zinc-600 animate-pulse')} />
                        <BookOpen className="h-3 w-3 text-brand-gold/70" />
                        {c}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Answer */}
              {phase === 'answer' && (
                <div className="self-start max-w-[92%] rounded-2xl rounded-tl-md border border-brand-gold/20 bg-brand-gold/[0.06] px-4 py-3 text-sm text-zinc-100 leading-relaxed">
                  {typed}
                  {typed.length < script.a.length && <span className="inline-block w-1.5 h-4 align-middle bg-brand-gold animate-pulse ml-0.5" />}
                  {typed.length >= script.a.length && (
                    <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between">
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Confidence <span className="text-brand-gold">{script.conf}%</span></div>
                      <div className="h-1 w-24 rounded-full bg-white/5 overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-brand-gold to-[#FFE17A]" style={{ width: `${script.conf}%` }} />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Follow-up chips */}
              {phase === 'answer' && typed.length >= script.a.length && (
                <div className="flex flex-wrap gap-2">
                  {script.chips.map((c) => (
                    <span key={c} className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.02] px-3 py-1 text-[11px] text-zinc-300">
                      <ChevronRight className="h-3 w-3 text-brand-gold/70" />
                      {c}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </MockDashboardChrome>
        </div>
      </Container>
    </section>
  )
}

export default AIIntelligence
